"use client";

import { useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { CheckCircle, AlertCircle, Loader2, FileText } from "lucide-react";
import whitepaperFormData from "@data/components/whitepaper-download-form.json";
import { trackError, trackFormSubmit } from "@utils/ga4Tracking";

const WHITEPAPER_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/20240903/4obp8nr/";

interface WhitepaperDownloadFormData {
  title: string;
  description: string;
  emailLabel: string;
  emailPlaceholder: string;
  consentLabel: string;
  submitText: string;
  submittingText: string;
  successTitle: string;
  successMessage: string;
  errorMessage: string;
}

interface WhitepaperDownloadFormProps {
  title: string;
  description: string;
  webhookSource?: string;
}

export default function WhitepaperDownloadForm({
  title,
  description,
  webhookSource,
}: WhitepaperDownloadFormProps) {
  const data = whitepaperFormData as WhitepaperDownloadFormData;
  const pathname = usePathname();
  const source =
    webhookSource ??
    (pathname
      ? `Whitepaper Anfrage ueber ${pathname}`
      : "Whitepaper Anfrage ueber Webseite DBI");

  const [email, setEmail] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }

    if (!gdprConsent) {
      setStatus("error");
      setErrorMessage(data.errorMessage);
      return;
    }

    setIsSubmitting(true);
    setStatus("idle");
    setErrorMessage("");

    try {
      const payload = {
        email: email.trim().toLowerCase(),
        gdprConsent: true,
        timestamp: new Date().toISOString(),
        source,
        formType: "whitepaper_request",
        submissionId: `whitepaper_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
        userAgent: navigator.userAgent,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      let response = await fetch(WHITEPAPER_WEBHOOK_URL, {
        method: "POST",
        headers: {
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok && response.status >= 400) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          formData.append(key, String(value));
        });

        response = await fetch(WHITEPAPER_WEBHOOK_URL, {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
          body: formData,
        });
      }

      if (!response.ok && response.status >= 400) {
        const queryParams = new URLSearchParams();
        Object.entries(payload).forEach(([key, value]) => {
          queryParams.append(key, String(value));
        });

        response = await fetch(`${WHITEPAPER_WEBHOOK_URL}?${queryParams.toString()}`, {
          method: "POST",
          headers: {
            Accept: "application/json",
          },
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      setStatus("success");
      setEmail("");
      setGdprConsent(false);
      trackFormSubmit("whitepaper_request", "whitepaper", true);
    } catch (error) {
      setStatus("error");
      setErrorMessage(data.errorMessage);
      trackFormSubmit("whitepaper_request", "whitepaper", false);
      trackError(
        "whitepaper_form_submission",
        error instanceof Error ? error.message : "Unknown error",
        "whitepaper"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
      <h3 className="mb-3 text-2xl font-bold text-abis-primary">{title}</h3>
      <p className="mb-6 text-gray-600">{description}</p>

      {status === "success" ? (
        <div className="rounded-xl border border-green-200 bg-green-50 p-5 text-center">
          <CheckCircle className="mx-auto mb-3 h-10 w-10 text-green-600" />
          <p className="mb-1 text-lg font-semibold text-green-800">{data.successTitle}</p>
          <p className="text-sm text-green-700">{data.successMessage}</p>
        </div>
      ) : (
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="whitepaper-email">
              {data.emailLabel}
            </label>
            <input
              id="whitepaper-email"
              type="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={data.emailPlaceholder}
              disabled={isSubmitting}
              className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:border-abis-primary focus:outline-none focus:ring-2 focus:ring-abis-primary/20 disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>

          <label htmlFor="whitepaper-consent" className="flex items-start gap-3 cursor-pointer group py-1">
            <input
              id="whitepaper-consent"
              type="checkbox"
              checked={gdprConsent}
              onChange={(event) => setGdprConsent(event.target.checked)}
              className="sr-only"
            />
            <div
              className={`mt-0.5 w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-all duration-200 ${
                gdprConsent
                  ? "bg-abis-primary"
                  : "bg-gray-100 border-2 border-gray-300 group-hover:border-abis-primary/50"
              }`}
            >
              {gdprConsent ? <CheckCircle className="w-3.5 h-3.5 text-white" /> : null}
            </div>
            <span className="text-xs text-gray-600 leading-relaxed group-hover:text-gray-700 transition-colors">
              {data.consentLabel}{" "}
              <Link
                href="/imprint#datenschutz"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-abis-primary underline"
              >
                Datenschutzerklaerung
              </Link>
              .
            </span>
          </label>

          {status === "error" ? (
            <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting || !gdprConsent}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-abis-primary px-4 py-3 text-base font-semibold text-white transition hover:bg-abis-primary/90 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                {data.submittingText}
              </>
            ) : (
              <>
                <FileText className="h-5 w-5" />
                {data.submitText}
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
